import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import ImageBackgroundOne from '@/assets/images/Vector.png';
import ImageBackgroundTwo from '@/assets/images/Vector1.png';
import ImageBackgroundOThree from '@/assets/images/Vector2.png';
import ImageBackgroundFour from '@/assets/images/Vector3.png';
import ImageBackgroundFive from '@/assets/images/Vector4.png';
import ImageBackgroundSix from '@/assets/images/Vector5.png';
import LogoEdu from '@/assets/images/LogoWhite.png';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React, { useState } from 'react';
import useLoadingActions from '@/hooks/useLoadingActions.tsx';
import { LoginCredentialsDTO, loginWithEmailAndPassword } from '@/features/auth/api/login.ts';
import { useUserStore } from '@/stores/user.ts';
import { enqueueSnackbar } from 'notistack';
import { UserModel } from '@/features/auth/types';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { getDefaultRouteForUser } from '@/utils/routePermissions';
import getApi from '@/lib/axios';

interface DecodedToken {
  user_id?: string;
  name?: string;
  username?: string;
  permissions?: string[];
}

const style = {
  '& .MuiInputBase-input': {
    border: '1px solid rgba(128, 128, 128, 1)',
    height: '32px',
    padding: '0 0 0 5px',
    borderRadius: '4px',
    paddingLeft: '10p',
  },
  '& .MuiOutlinedInput-root': {
    color: 'rgba(30, 30, 30, 1)',

    '& fieldset': {
      borderColor: '#E0E3E7',
    },
    '&:hover fieldset': {
      borderColor: '#B2BAC2',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6F7E8C',
    },
  },
};

const Login = () => {
  const navigate = useNavigate();
  const { setToken, setRefreshToken, setUpUserData } = useUserStore();
  const { setIsLoading } = useLoadingActions();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      setIsLoading(true, 'Carregando');

      const data = new FormData(event.currentTarget);
      const auth = await loginWithEmailAndPassword({
        username: data.get('username'),
        password: data.get('password'),
      } as LoginCredentialsDTO);

      const tokens = auth.data.tokens;

      const decoded = jwtDecode(tokens.access) as DecodedToken;

      setToken(tokens.access);
      setRefreshToken(tokens.refresh);
      setUpUserData({
        id: String(decoded?.user_id || ''),
        name: decoded?.name || '',
        username: decoded?.username || '',
        email: '', // Será preenchido posteriormente se necessário
        permissions: [], // Será carregado via API
      } as UserModel);

      // Busca as permissões da API após o login
      try {
        const api = await getApi();
        const permissionsResponse = await api.get('/get_permissions/');
        const userPermissions = permissionsResponse.data || [];
        
        // Atualiza o usuário com as permissões da API
        setUpUserData({
          id: String(decoded?.user_id || ''),
          name: decoded?.name || '',
          username: decoded?.username || '',
          email: '',
          permissions: userPermissions,
        } as UserModel);

        // Redireciona para a primeira página permitida baseada nas permissões da API
        const defaultRoute = getDefaultRouteForUser(userPermissions);
        return navigate(defaultRoute);
      } catch (permissionError) {
        console.error('Erro ao buscar permissões:', permissionError);
        // Se falhar ao buscar permissões, redireciona para dashboard
        return navigate('/dashboard');
      }
    } catch (e) {
      enqueueSnackbar('Usuário ou senha inválidos', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setIsLoading(false, '');
    }
  };

  return (
    <Box
      position={'relative'}
      sx={{ bgcolor: '#E04A4C', height: '100vh' }}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <img style={{ position: 'absolute', top: 0, left: 0 }} src={ImageBackgroundOne} alt="" />
      <img style={{ position: 'absolute', bottom: 0, left: 0 }} src={ImageBackgroundTwo} alt="" />
      <img style={{ position: 'absolute', bottom: 0, right: 0 }} src={ImageBackgroundOThree} alt="" />
      <img style={{ position: 'absolute', top: 0, right: 0 }} src={ImageBackgroundFour} alt="" />
      <img style={{ position: 'absolute', bottom: 0, right: 0 }} src={ImageBackgroundFive} alt="" />
      <img style={{ position: 'absolute', bottom: 0, right: 0 }} src={ImageBackgroundSix} alt="" />
      <Stack zIndex={1} alignItems={'center'}>
        <img src={LogoEdu} alt="" />

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Stack
            sx={{
              width: '352px',
              bgcolor: '#FFF',
              padding: '24px',
              borderRadius: '8px',
              color: '#000',
              boxShadow: '0px 0px 16px 4px #BE3F40'
            }}
            gap={'8px'}
          >
            <Typography>Email*</Typography>
            <TextField sx={style} required id="username" name="username" />
            <Typography>Senha*</Typography>
            <TextField type={showPassword ? 'text' : 'password'} sx={style} required id="password" name="password" />
            <InputAdornment
              sx={{
                '.MuiButtonBase-root': {
                  marginTop: '-50px',
                  marginLeft: '250px',
                },
              }}
              position="end"
            >
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
            <FormControlLabel control={<Checkbox defaultChecked />} label="Lembre de mim" />
            <Button
              type="submit"
              sx={{ p: '7.5px 0', borderRadius: '24px', fontSize: '14px', background: 'rgba(21, 171, 81, 1)', textTransform: 'none' }}
              variant="contained"
            >
              Entrar
            </Button>
            <Typography
              mt={'16.5px'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={1}
            >
              Esqueceu a senha?
              <Link href="/register" underline="always" color={'#E04A4C'} fontWeight={'bold'}>
                Recuperar senha
              </Link>
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default Login;
